import React, { useState, useEffect, useRef } from 'react';
import { ShoppingBag, Send, ChevronDown, Sparkles, ArrowRight, Phone } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useContactModal } from '../contexts/ContactModalContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiUrl } from '../lib/apiBase';
import OrderDrawer from './OrderDrawer';

interface MenuLink {
  label: string;
  href: string;
}

interface MenuColumn {
  title: string;
  items: MenuLink[];
  /** Render this group above the next one in a single column. Short groups
      (two or three links) would otherwise leave a tall empty column. */
  stackWithNext?: boolean;
}

interface FeaturedCard {
  href: string;
  image: string;
  kicker: string;
  title: string;
  text: string;
}

interface MegaMenu {
  columns: MenuColumn[];
  viewAll: MenuLink;
  /** Blog category whose two latest posts fill the featured cards. */
  blogCategory?: string;
  /** Shown until the blog posts load, or when there are none. */
  fallbackCards: FeaturedCard[];
}

interface NavigationItem {
  label: string;
  href: string;
  menu?: MegaMenu;
  /** The vehicles menu is picture cards only, kept as the client asked. */
  vehicleMenu?: FeaturedCard[];
}

interface BlogPostSummary {
  slug: string;
  title: string;
  excerpt?: string;
  category: string;
  featuredImage?: string | null;
  featured?: boolean;
}

const TOP_BAR_ITEMS = ['No minimum order', 'Bulk discounts', 'In-house design', 'Kidderminster showroom'];

/** Groups the flat column list into visual columns, honouring `stackWithNext`. */
const visualColumns = (columns: MenuColumn[]): MenuColumn[][] => {
  const out: MenuColumn[][] = [];
  let current: MenuColumn[] = [];
  columns.forEach((column) => {
    current.push(column);
    if (!column.stackWithNext) {
      out.push(current);
      current = [];
    }
  });
  if (current.length) out.push(current);
  return out;
};

/** Clothing links use the /clothing browser's `types`, `ages` and `genders` params. */
const clothing = (params: Record<string, string>) =>
  `/clothing?${Object.entries(params)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&')}`;

const signageMenu: MegaMenu = {
  columns: [
    {
      title: 'Walls',
      items: [
        { label: 'Signboards', href: '/services/signboards' },
        { label: 'PVC Banners', href: '/services/pvc-banners' },
        { label: 'Projecting Signs', href: '/services/projecting-signs' },
        { label: 'Raised Lettering', href: '/services/raised-lettering' },
      ],
    },
    {
      title: 'Windows',
      items: [
        { label: 'Privacy Film', href: '/services/window-privacy-film' },
        { label: 'Window Graphics', href: '/services/window-graphics' },
        { label: 'Glass Manifestation', href: '/services/glass-manifestation' },
      ],
    },
    {
      title: 'Freestanding',
      items: [
        { label: 'Pavement Signs', href: '/services/pavement-signs' },
        { label: 'Pull Up Banners', href: '/services/pull-up-banners' },
        { label: 'Heras Fencing Banners', href: '/services/heras-fencing-banners' },
        { label: 'Event Boards', href: '/services/event-boards' },
      ],
    },
  ],
  viewAll: { label: 'View all signage', href: '/services/all-signage' },
  blogCategory: 'signage',
  fallbackCards: [
    {
      href: '/services/all-signage',
      image: '/home/signage/signboard-kearns.jpg',
      kicker: 'Featured',
      title: 'Business signage',
      text: 'Premium signs for your premises',
    },
    {
      href: '/blog',
      image: '/home/signage/projecting-kearns.jpg',
      kicker: 'From the blog',
      title: 'Signage guide',
      text: 'Choosing the right signs for you',
    },
  ],
};

const clothingMenu: MegaMenu = {
  columns: [
    {
      title: 'Tops',
      items: [
        { label: 'T-Shirts', href: clothing({ types: 'T-Shirts' }) },
        { label: 'Polos', href: clothing({ types: 'Polos' }) },
        { label: 'Shirts', href: clothing({ types: 'Shirts' }) },
        { label: 'Vests', href: clothing({ types: 'Vests (t-shirt)' }) },
        { label: 'Sweatshirts & Hoodies', href: clothing({ types: 'Sweatshirts,Hoodies' }) },
        { label: 'Outerwear', href: clothing({ types: 'Jackets,Gilets & Body Warmers,Fleece,Softshells' }) },
      ],
    },
    {
      title: 'Bottoms',
      stackWithNext: true,
      items: [
        { label: 'Trousers', href: clothing({ types: 'Trousers,Chinos' }) },
        { label: 'Shorts', href: clothing({ types: 'Shorts' }) },
      ],
    },
    {
      title: 'Junior',
      items: [
        { label: '0-3 years', href: clothing({ ages: 'Infant' }) },
        { label: '4-13 years', href: clothing({ ages: 'Kids' }) },
      ],
    },
    {
      title: 'Collections',
      items: [
        { label: 'Hats', href: clothing({ types: 'Caps,Hats,Beanies' }) },
        { label: 'Aprons', href: clothing({ types: 'Aprons' }) },
        { label: 'Bags', href: clothing({ types: 'Bags' }) },
        { label: 'Activewear', href: clothing({ types: 'Sports Overtops,Baselayers,Leggings,Trackwear,Sweatpants' }) },
        { label: 'Womens', href: clothing({ genders: 'Female' }) },
        { label: 'Safetywear', href: clothing({ types: 'Safety Vests' }) },
      ],
    },
  ],
  viewAll: { label: 'Browse all clothing', href: '/clothing' },
  blogCategory: 'clothing',
  fallbackCards: [
    {
      href: '/clothing',
      image: '/home/hero/clothing.jpg',
      kicker: 'Featured',
      title: 'Custom clothing',
      text: 'Browse our full range',
    },
    {
      href: '/shop',
      image: '/home/hero/clothing-alt.jpg',
      kicker: 'How it works',
      title: 'Customisation',
      text: 'Print, embroidery and more',
    },
  ],
};

/** Printing links: `/printing/all/<slug>` opens one product, `?q=` filters by name, `?category=` by catalogue category. */
const printingMenu: MegaMenu = {
  columns: [
    {
      title: 'Business',
      items: [
        { label: 'Business Cards', href: '/printing/all?q=business%20card' },
        { label: 'Booklets', href: '/printing/all/booklets-and-brochures' },
        { label: 'Appointment Cards', href: '/printing/all?q=appointment%20card' },
        { label: 'Loyalty Cards', href: '/printing/all?q=loyalty%20card' },
        { label: 'Calendars', href: '/printing/all?category=calendars' },
        { label: 'Notepads', href: '/printing/all?q=note' },
      ],
    },
    {
      title: 'Promotional',
      items: [
        { label: 'Flyers', href: '/printing/all?q=flyer' },
        { label: 'Scratch Cards', href: '/printing/all/scratch-cards' },
        { label: 'Gift Vouchers', href: '/printing/all/printed-gift-vouchers' },
        { label: 'Posters', href: '/printing/all/printed-posters' },
        { label: 'Invitations', href: '/printing/all/invitations' },
        { label: 'Greetings Cards', href: '/printing/all/printed-greetings-cards' },
      ],
    },
    {
      title: 'Packaging',
      items: [
        { label: 'Belly Bands', href: '/printing/all/packaging-sleeves-and-belly-bands' },
        { label: 'Product Labels', href: '/printing/all/adhesive-product-labels' },
        { label: 'Sticker Sheets', href: '/printing/all?q=sticker%20sheet' },
        { label: 'Swing Tags', href: '/printing/all/clothing-swing-tags' },
        { label: 'Backing Cards', href: '/printing/all?q=backing%20card' },
        { label: 'Postcards', href: '/printing/all/printed-postcards' },
      ],
    },
  ],
  viewAll: { label: 'Browse all printing', href: '/printing/all' },
  blogCategory: 'print-guides',
  fallbackCards: [
    {
      href: '/printing/all',
      image: '/home/hero/printing.jpg',
      kicker: 'Featured',
      title: 'Print services',
      text: 'Quality printing for your business',
    },
    {
      href: '/printing',
      image: '/home/print/business-cards.jpg',
      kicker: 'Learn more',
      title: 'About printing',
      text: 'Discover our print capabilities',
    },
  ],
};

const displayMenu: MegaMenu = {
  columns: [
    {
      title: 'Indoor',
      items: [
        { label: 'Pull Up Banners', href: '/services/pull-up-banners' },
        { label: 'Pop Up Displays', href: '/services/pop-up-displays' },
        { label: 'Carpet Mats', href: '/services/carpet-mats' },
        { label: 'Event Boards', href: '/services/event-boards' },
      ],
    },
    {
      title: 'Outdoor',
      items: [
        { label: 'Feather Flags', href: '/services/feather-flags' },
        { label: 'Cafe Barriers', href: '/services/cafe-barriers' },
        { label: 'Gazebos', href: '/services/gazebos' },
        { label: 'Parasols', href: '/services/parasols' },
      ],
    },
    {
      title: 'On the table',
      items: [
        { label: 'Tablecloths', href: '/services/tablecloths' },
        { label: 'Tabletop POS', href: '/services/tabletop-pos' },
      ],
    },
  ],
  viewAll: { label: 'Browse all display', href: '/services/all-display' },
  blogCategory: 'display',
  fallbackCards: [
    {
      href: '/services/all-display',
      image: '/home/display/oc-flag.jpg',
      kicker: 'Featured',
      title: 'Event display',
      text: 'Flags, gazebos and stands',
    },
    {
      href: '/services/gazebos',
      image: '/home/gazebos/gazebo-header.jpg',
      kicker: 'Featured',
      title: 'Branded gazebos',
      text: 'A blank canvas for your brand',
    },
  ],
};

const vehicleCards: FeaturedCard[] = [
  {
    href: '/services/vehicle-signwriting',
    image: '/vehicle-signwriting/vehicle1.jpg',
    kicker: 'Featured',
    title: 'Vehicle Signwriting',
    text: 'Custom graphics for your fleet',
  },
  {
    href: '/services/magnetic-panels',
    image: '/home/vehicles/magnetic-hero.jpg',
    kicker: 'Featured',
    title: 'Magnetic Panels',
    text: 'Removable signs for any vehicle',
  },
  {
    href: '/blog/vehicle-branding',
    image: '/vehicle-signwriting/vehicle5.jpg',
    kicker: 'From the blog',
    title: 'Branding Guide',
    text: 'Maximise your mobile advertising',
  },
];

const navigationItems: NavigationItem[] = [
  { label: 'HOME', href: '/' },
  { label: 'SIGNAGE', href: '/services/all-signage', menu: signageMenu },
  { label: 'CLOTHING', href: '/clothing', menu: clothingMenu },
  { label: 'VEHICLES', href: '/services/vehicle-signwriting', vehicleMenu: vehicleCards },
  { label: 'PRINTING', href: '/printing', menu: printingMenu },
  { label: 'DISPLAY', href: '/services/all-display', menu: displayMenu },
];

interface HeaderProps {
  className?: string;
}

const Header: React.FC<HeaderProps> = ({ className = '' }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileMenuState, setMobileMenuState] = useState<Record<string, boolean>>({});
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [blogPosts, setBlogPosts] = useState<BlogPostSummary[]>([]);
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  const { cart } = useCart();
  const { openContactModal } = useContactModal();
  const { colorScheme, colors } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  useEffect(() => {
    document.body.style.paddingTop = headerHeight ? `${headerHeight}px` : '';
    document.documentElement.style.setProperty('--header-h', `${headerHeight}px`);
    return () => {
      document.body.style.paddingTop = '';
      document.documentElement.style.removeProperty('--header-h');
    };
  }, [headerHeight]);

  // One fetch fills the "from the blog" cards in every mega menu. Only posts
  // with a photo are usable; the static cards stay when a category has none.
  useEffect(() => {
    let cancelled = false;
    fetch(apiUrl('blog/posts'))
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data?.success || !Array.isArray(data.posts)) return;
        setBlogPosts(data.posts.filter((p: BlogPostSummary) => p.featuredImage));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleMobileSubmenu = (key: string) => {
    setMobileMenuState((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const featuredCardsFor = (menu: MegaMenu): FeaturedCard[] => {
    const posts = blogPosts.filter((p) => p.category === menu.blogCategory).slice(0, 2);
    if (posts.length < 2) return menu.fallbackCards;
    return posts.map((p) => ({
      href: `/blog/${p.slug}`,
      image: p.featuredImage as string,
      kicker: 'From the blog',
      title: p.title,
      text: p.excerpt ?? '',
    }));
  };

  const renderCard = (card: FeaturedCard, delay: number, height = 'h-[120px]') => (
    <a
      key={card.href + card.title}
      href={card.href}
      className={`group/card relative rounded-xl overflow-hidden opacity-0 animate-[fadeSlideIn_0.3s_ease-out_forwards] ${height}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover/card:scale-110"
        style={{ backgroundImage: `url(${card.image})` }}
      />
      <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${colors.megaMenuBg}, ${colors.megaMenuBg}b3, transparent)` }} />
      <div className="relative h-full p-4 flex flex-col justify-end">
        <span className="embossing-font text-[9px] uppercase tracking-[0.12em] mb-1" style={{ color: colors.accent }}>{card.kicker}</span>
        <h4 className="hearns-font text-lg text-white mb-0.5 leading-tight line-clamp-1">{card.title}</h4>
        <p className="grotesk-font text-[11px] text-white/60 line-clamp-1">{card.text}</p>
      </div>
    </a>
  );

  // Panels are sized by their content (w-max) and centred under the nav, not the
  // hovered item, so the last items never push a panel off the right edge. They
  // also reset the nav's semibold letter-spaced type, which made every row heavy.
  const panelWrap =
    'absolute left-1/2 top-full z-50 w-max -translate-x-1/2 pt-3 font-normal tracking-normal text-left opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300 ease-out';

  const renderMegaMenu = (menu: MegaMenu) => (
    <div className={panelWrap}>
      <div className="rounded-2xl shadow-[0_25px_60px_rgba(0,0,0,0.5)] overflow-hidden" style={{ backgroundColor: colors.megaMenuBg }}>
        <div className="grid grid-cols-[auto_250px]">
          <div className="px-7 py-6 border-r border-white/10">
            <div className="flex gap-12">
              {visualColumns(menu.columns).map((groups, ci) => (
                <div key={groups[0].title} className="shrink-0 flex flex-col gap-6">
                  {groups.map((column) => (
                    <div key={column.title}>
                      <h3 className="embossing-font text-[10px] uppercase tracking-[0.15em] mb-3 pb-2 border-b border-white/10" style={{ color: colors.accent }}>{column.title}</h3>
                      <ul>
                        {column.items.map((item, index) => (
                          <li key={item.label} className="opacity-0 animate-[fadeSlideIn_0.25s_ease-out_forwards]" style={{ animationDelay: `${ci * 60 + index * 25}ms` }}>
                            <a href={item.href} className="group/link -ml-4 flex items-center gap-1.5 py-1.5 pl-4 grotesk-font text-[13px] whitespace-nowrap text-white/60 hover:text-white transition-colors">
                              <ArrowRight
                                className="h-3 w-3 shrink-0 -ml-[18px] text-[#78BE20] opacity-0 -translate-x-1 group-hover/link:opacity-100 group-hover/link:translate-x-0 transition-all duration-200"
                                strokeWidth={2.5}
                              />
                              <span>{item.label}</span>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between gap-8 pt-4 mt-5 border-t border-white/10 opacity-0 animate-[fadeSlideIn_0.3s_ease-out_forwards]" style={{ animationDelay: '300ms' }}>
              <span className="grotesk-font text-[12px] text-white/40 whitespace-nowrap">
                Not sure what you need?{' '}
                <a href="tel:01562227117" className="text-white/70 hover:text-white transition-colors">Call 01562 227 117</a>
              </span>
              <a
                href={menu.viewAll.href}
                className="inline-flex items-center gap-2 bg-[#78BE20] hover:bg-[#64A70B] text-white font-poppins font-semibold text-[11px] tracking-[0.12em] uppercase px-5 py-2.5 whitespace-nowrap transition-colors"
              >
                {menu.viewAll.label}
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} />
              </a>
            </div>
          </div>

          <div className="p-4">
            <div className="flex flex-col gap-3">
              {featuredCardsFor(menu).map((card, i) => renderCard(card, 50 + i * 50))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderVehicleMegaMenu = (cards: FeaturedCard[]) => (
    <div className={panelWrap}>
      <div className="rounded-2xl shadow-[0_25px_60px_rgba(0,0,0,0.5)] overflow-hidden" style={{ backgroundColor: colors.megaMenuBg }}>
        <div className="p-4">
          <div className="grid grid-cols-3 gap-3 w-[720px]">
            {cards.map((card, i) => renderCard(card, i * 80, 'h-[150px]'))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Font declarations for mega menus */}
      <style>{`
        .embossing-font {
          font-family: 'Montserrat', sans-serif; font-weight: 800;
          letter-spacing: 0.12em;
        }
        .grotesk-font {
          font-family: 'Montserrat', sans-serif;
        }
        .hearns-font {
          font-family: 'Montserrat', sans-serif; font-weight: 800;
        }
        .aldivaro-font {
          font-family: 'Montserrat', sans-serif; font-weight: 800;
        }
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-50 pt-[env(safe-area-inset-top)] transition-all ${isScrolled ? 'backdrop-blur-md' : ''} ${className}`}
        style={{ backgroundColor: isScrolled ? `${colors.headerBg}f2` : colors.headerBg }}
      >
        {/* Green strapline bar */}
        <div className="bg-[#78BE20] text-white">
          <ul className="max-w-[1600px] mx-auto px-4 lg:px-8 h-8 flex items-center justify-center gap-x-3 sm:gap-x-5 font-poppins font-semibold text-[10px] sm:text-[11px] tracking-[0.08em] uppercase whitespace-nowrap overflow-hidden">
            {TOP_BAR_ITEMS.map((item, i) => (
              <li key={item} className={`flex items-center gap-x-3 sm:gap-x-5 ${i > 1 ? 'hidden sm:flex' : 'flex'}`}>
                {i > 0 && <span className={`w-px h-3 bg-white/60 ${i === 2 ? 'hidden sm:block' : ''}`} aria-hidden="true" />}
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Compact Single-Line Header */}
        <header className="border-b border-white/10">
          <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
            <div className="flex items-center justify-between h-14">

            {/* Left: Logo */}
            <a href="/" className="flex-shrink-0 relative group">
              <img
                src="/images/outpost-logo.png"
                alt="Outpost Custom"
                className="h-8 w-auto transition-transform group-hover:scale-105"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const parent = e.currentTarget.parentElement;
                  if (parent) {
                    const textLogo = document.createElement('span');
                    textLogo.textContent = 'OUTPOST';
                    textLogo.className = 'text-xl font-bold tracking-tight';
                    textLogo.style.color = '#6da71d';
                    parent.appendChild(textLogo);
                  }
                }}
              />
            </a>

            {/* Center: Navigation */}
            <nav className="relative hidden lg:flex flex-1 justify-center px-3">
              <ul className="flex items-center gap-2.5 xl:gap-4 2xl:gap-5 text-[11px] xl:text-[13px] font-semibold tracking-[0.06em] xl:tracking-[0.1em] 2xl:tracking-[0.12em] whitespace-nowrap text-white/65">
                {navigationItems.map((item) => (
                  <li key={item.label} className="group">
                    <a
                      href={item.href}
                      className="flex items-center gap-1.5 border-b border-transparent pb-1 text-white/70 transition-colors hover:border-lime-400/70 hover:text-white"
                    >
                      <span>{item.label}</span>
                      {(item.menu || item.vehicleMenu) && (
                        <ChevronDown className="h-3 w-3 text-white/50 transition-transform duration-200 group-hover:rotate-180" strokeWidth={2.5} />
                      )}
                    </a>
                    {item.menu && renderMegaMenu(item.menu)}
                    {item.vehicleMenu && renderVehicleMegaMenu(item.vehicleMenu)}
                  </li>
                ))}
              </ul>
            </nav>

            {/* Right: Contact Info + Icons */}
            <div className="flex items-center gap-2 xl:gap-3 shrink-0">

              {/* Phone + Quote Basket */}
              <div className="flex items-center gap-3">
                {/* Phone number */}
                <a
                  href="tel:01562227117"
                  className="hidden 2xl:flex items-center gap-1.5 text-white/60 hover:text-white transition-colors whitespace-nowrap"
                >
                  <Phone className="w-3.5 h-3.5" strokeWidth={2} />
                  <span className="text-xs font-medium tracking-wide">01562 227 117</span>
                </a>

                {/* Below lg only: from lg the YOUR QUOTE button beside it opens the
                    same basket and carries the same count, so showing both was
                    two controls doing one job. */}
                <button
                  onClick={() => setIsCartOpen(true)}
                  className="relative lg:hidden p-2 hover:bg-white/5 rounded-lg transition-colors"
                  aria-label={cart.length > 0 ? `Quote basket, ${cart.length} item${cart.length === 1 ? '' : 's'}` : 'Quote basket'}
                  title="Quote basket"
                >
                  {/* A bag, not a plus: a "+" gave no clue that this opens the basket. */}
                  <ShoppingBag
                    className="w-5 h-5 transition-colors"
                    style={{
                      color: cart.length > 0
                        ? (colorScheme === 'purple' ? colors.iconColor : '#6da71d')
                        : (colorScheme === 'purple' ? colors.iconColor : 'rgba(255,255,255,0.5)')
                    }}
                  />
                  {cart.length > 0 && (
                    <span
                      className="absolute -top-1 -right-1 w-4 h-4 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                      style={{ backgroundColor: colorScheme === 'purple' ? colors.secondary : '#6da71d' }}
                    >
                      {cart.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Quote basket CTA. It used to link to /contact?type=quote, which is
                  not a route — there is no /contact page — so it went nowhere. It
                  opens the basket, the same as the bag icon, which is the small-screen
                  affordance for the same thing. Flat and square like GreenButton,
                  darkening on hover, at the client's request (2026-09-26). */}
              <button
                onClick={() => setIsCartOpen(true)}
                className="hidden lg:inline-flex items-center gap-2 px-2.5 xl:px-4 py-1.5 text-xs font-bold uppercase tracking-[0.12em] 2xl:tracking-[0.22em] whitespace-nowrap text-white bg-[#78BE20] border border-[#78BE20] hover:bg-[#64A70B] hover:border-[#64A70B] transition-colors duration-300 active:translate-y-[1px]"
                aria-label={cart.length > 0 ? 'Your quote' : 'Request a quote'}
                title={cart.length > 0 ? 'Your quote' : 'Request a quote'}
              >
                {/* Label only from xl; at lg the row is too tight for six nav items plus words. */}
                <span className="hidden xl:inline">{cart.length > 0 ? 'YOUR QUOTE' : 'REQUEST QUOTE'}</span>
                {cart.length > 0 ? (
                  <span className="inline-flex h-4 min-w-4 px-1 items-center justify-center rounded-full bg-white/25 text-[10px] tabular-nums">
                    {cart.length}
                  </span>
                ) : (
                  <ShoppingBag className="h-3.5 w-3.5" strokeWidth={2.5} />
                )}
              </button>

              {/* Contact Us CTA */}
              <button
                onClick={openContactModal}
                className="hidden lg:inline-flex items-center gap-2 rounded-[5px] px-2.5 xl:px-4 py-1.5 text-xs font-bold uppercase tracking-[0.12em] 2xl:tracking-[0.22em] whitespace-nowrap text-white/80 border border-white/20 hover:text-white hover:border-white/40 transition-all active:translate-y-[1px]"
                aria-label="Contact us"
                title="Contact us"
              >
                <span className="hidden xl:inline">CONTACT US</span>
                <Send className="h-3.5 w-3.5" strokeWidth={2.5} />
              </button>

              {/* Mobile Menu Toggle */}
              <button
                onClick={toggleMobileMenu}
                className="lg:hidden p-2 hover:bg-white/5 rounded-lg transition-colors"
                aria-label="Menu"
              >
                <div className="w-5 h-4 flex flex-col justify-between">
                  <span className={`block h-0.5 bg-white transition-all ${isMobileMenuOpen ? 'rotate-45 translate-y-1.5' : ''}`}></span>
                  <span className={`block h-0.5 bg-white transition-all ${isMobileMenuOpen ? 'opacity-0' : ''}`}></span>
                  <span className={`block h-0.5 bg-white transition-all ${isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </header>
    </div>

      {/* Mobile Navigation */}
      <div
        className={`fixed inset-0 z-40 transition-all duration-300 lg:hidden ${
          isMobileMenuOpen
            ? 'opacity-100 visible'
            : 'opacity-0 invisible pointer-events-none'
        }`}
        style={{ top: `${headerHeight}px`, backgroundColor: colors.headerBg }}
      >
        <div className="h-full overflow-y-auto pb-8">
          <div className="px-4 py-4">
            {/* Navigation Items */}
            <ul className="space-y-1">
              {navigationItems.map((item) => {
                const columns: MenuColumn[] = item.menu
                  ? item.menu.columns
                  : item.vehicleMenu
                    ? [{ title: 'Our services', items: item.vehicleMenu.map((c) => ({ label: c.title, href: c.href })) }]
                    : [];
                const viewAll = item.menu?.viewAll;
                return (
                  <li key={`mobile-${item.label}`}>
                    {columns.length > 0 ? (
                      <div className="rounded-xl overflow-hidden">
                        {/* Accordion Header */}
                        <button
                          type="button"
                          className="w-full flex items-center justify-between px-4 py-3 rounded-xl transition-colors"
                          style={{
                            backgroundColor: mobileMenuState[item.label]
                              ? `${colors.secondary}66`
                              : `${colors.secondary}33`
                          }}
                          onClick={() => toggleMobileSubmenu(item.label)}
                        >
                          <span className="hearns-font text-lg uppercase tracking-[0.1em] text-white">
                            {item.label}
                          </span>
                          <ChevronDown
                            className={`h-5 w-5 transition-transform duration-200 ${
                              mobileMenuState[item.label] ? 'rotate-180' : ''
                            }`}
                            style={{ color: colors.accent }}
                            strokeWidth={2}
                          />
                        </button>

                        {/* Accordion Content */}
                        <div
                          className={`overflow-hidden transition-all duration-300 ${
                            mobileMenuState[item.label]
                              ? 'max-h-[2000px] opacity-100'
                              : 'max-h-0 opacity-0'
                          }`}
                        >
                          <div className="px-2 py-3 space-y-4">
                            {item.label === 'PRINTING' && (
                              <a
                                href={item.href}
                                className="mx-2 flex items-center gap-2 rounded-lg border border-white/10 px-3 py-3 font-poppins text-sm font-semibold text-white hover:bg-white/5"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                <ArrowRight className="h-4 w-4" style={{ color: colors.accent }} strokeWidth={2} />
                                About printing
                              </a>
                            )}
                            {columns.map((column) => (
                              <div key={`${item.label}-${column.title}`}>
                                <p className="embossing-font text-[10px] uppercase tracking-[0.15em] mb-2 px-2" style={{ color: colors.accent }}>
                                  {column.title}
                                </p>
                                <div className="space-y-1">
                                  {column.items.map((link) => (
                                    <a
                                      key={link.label}
                                      href={link.href}
                                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                                      onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                      <ArrowRight className="h-3 w-3" style={{ color: colors.accent }} strokeWidth={2} />
                                      <span className="grotesk-font text-sm">{link.label}</span>
                                    </a>
                                  ))}
                                </div>
                              </div>
                            ))}
                            {viewAll && (
                              <a
                                href={viewAll.href}
                                className="mx-2 flex items-center justify-center gap-2 bg-[#78BE20] text-white font-poppins font-semibold text-[11px] tracking-[0.12em] uppercase px-4 py-3"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                {viewAll.label}
                                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <a
                        href={item.href}
                        className="flex items-center px-4 py-3 rounded-xl transition-colors"
                        style={{ backgroundColor: `${colors.secondary}33` }}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <span className="hearns-font text-lg uppercase tracking-[0.1em] text-white">
                          {item.label}
                        </span>
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>

            {/* Contact CTA. /contact is not a route either, so this opens the
                site-wide quote form, which is what the desktop CONTACT US does. */}
            <div className="mt-6 px-2">
              <button
                type="button"
                className="flex w-full items-center justify-center gap-2 rounded-xl px-5 py-4 hearns-font text-base uppercase tracking-[0.12em] text-white shadow-lg transition-all active:scale-[0.98]"
                style={{ backgroundColor: colors.buttonPrimary }}
                onClick={() => { setIsMobileMenuOpen(false); openContactModal(); }}
              >
                Get In Touch
                <Send className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>

            {/* Quick Actions */}
            <div className="mt-4 flex gap-2 px-2">
              <a
                href="/clothing"
                className="flex-1 flex items-center justify-center gap-2 rounded-lg border border-white/10 px-4 py-3 grotesk-font text-xs text-white/70 hover:text-white transition-all"
                style={{ backgroundColor: `${colors.secondary}4d` }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Sparkles className="h-4 w-4" style={{ color: colors.accent }} strokeWidth={2} />
                Browse Clothing
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Order Drawer */}
      <OrderDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />

    </>
  );
};

export default Header;
